import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  FecalScoring,
  FecalScoringModuleState,
  UpsertFecalScoringResponse,
} from '../../model/fecal-scoring.model';
import { FecalScoringService } from '../../services/fecal-scoring.service';

import * as actions from '../actions/fecal-scoring.actions';

@Injectable()
export class FecalScoringEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store<FecalScoringModuleState>,
    private readonly fecalScoringService: FecalScoringService,
    private readonly dialogService: DialogService,
    private readonly localeService: LocaleService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      exhaustMap(() => of(actions.clearFecalScoringState())),
    ),
  );

  getFecalScorings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getFecalScoringsRequest),
      exhaustMap((_) =>
        this.fecalScoringService.getFecalScorings().pipe(
          switchMap((fecalScorings: FecalScoring[]) =>
            of(actions.getFecalScoringsSuccess({ fecalScorings })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getFecalScoringsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedFecalScore$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedFecalScoringRequest),
      exhaustMap(({ id }) =>
        this.fecalScoringService.getFecalScoringById(id).pipe(
          switchMap((fecalScoring: FecalScoring) =>
            of(actions.setSelectedFecalScoringSuccess({ fecalScoring })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.setSelectedFecalScoringError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertFecalScoring$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertFecalScoringRequest),
      mergeMap((action) =>
        this.fecalScoringService.upsertFecalScoring(action.request).pipe(
          switchMap((response: UpsertFecalScoringResponse) =>
            of(
              actions.upsertFecalScoringSuccess({ request: action.request, response }),
              actions.getFecalScoringsRequest(),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('fecal-score-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertFecalScoringError({ error }),
              actions.clearFecalScoringState(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFecalScoringSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.upsertFecalScoringSuccess),
        tap((action) => {
          if (
            !action.response.isOffline &&
            action.request.id === undefined &&
            action.response.id > 0
          ) {
            this.store.dispatch(actions.setSelectedFecalScoringRequest({ id: action.response.id }));
          } else {
            this.store.dispatch(actions.clearSelectedFecalScoring());
          }
        }),
      ),
    { dispatch: false },
  );

  deleteFecalScoringWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteFecalScoringWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('fecal-scoring.overview.delete-confirmation.title'),
            text: this.localeService.translate(
              'fecal-scoring.overview.delete-confirmation.message',
            ),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteFecalScoringRequest({
                    fecalScoring: action.fecalScoring,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteFecalScoringRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteFecalScoringRequest),
      mergeMap((action) =>
        this.fecalScoringService.deleteFecalScoring(action.fecalScoring.id).pipe(
          map(() => actions.deleteFecalScoringSuccess({ fecalScoringId: action.fecalScoring.id })),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.localeService.translate(
                  'fecal-scoring.overview.delete-error-message',
                ),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteFecalScoringSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteFecalScoringSuccess),
        tap((_) => this.router.navigate(['/fecal-scoring'])),
      ),
    { dispatch: false },
  );

  navigateCreateFecalScoring$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.navigateCreateFecalScoring),
        tap((action) => this.router.navigate(['/fecal-scoring/create', action.locationId])),
      ),
    {
      dispatch: false,
    },
  );
}
